// ============================================================================
// FSA POLICY NOTE — adapter over the shared sectioned-narrative editor
// ============================================================================
//
// Used by the Note Modal when hydrate.formatType === 'POLICY' (Accounting
// Policies, Corporate Information, Additional Information). The section
// rail + TipTap editor live in SectionedNarrativeEditor (shared with the
// ADR-0024 sectioned Board's Report); this adapter only maps the note
// workspace keys:
//   policy_section_data   ↔ sectionData
//   added_policy_sections ↔ addedSections
//   policy_section_order  ↔ order
// and reads the template sections from the schedule's column_spec.

import React, { useCallback, useMemo } from 'react';
import SectionedNarrativeEditor, {
  type SectionedState,
  type SectionTemplate,
} from './SectionedNarrativeEditor';
import type {
  NoteHydrate,
  NoteWorkspaceData,
  PolicySectionEntry,
  NoteAddedPolicySection,
} from 'api/types/fsa/noteData.types';

interface Props {
  hydrate: NoteHydrate;
  workspace: NoteWorkspaceData;
  setWorkspace: (next: NoteWorkspaceData) => void;
  readOnly?: boolean;
  /**
   * Bumped by the Note Modal each time it loads the saved workspace. The
   * TipTap editor reads its content only at creation, and this pane is
   * mounted (hidden) before the saved workspace arrives — keying the
   * editor on this forces a remount with the saved content instead of
   * the template default.
   */
  contentVersion?: number;
}

const PolicyEditorPane: React.FC<Props> = ({ hydrate, workspace, setWorkspace, readOnly = false, contentVersion = 0 }) => {
  const templateSections = useMemo<SectionTemplate[]>(
    () => parseTemplateSections(hydrate.columnSpecJson),
    [hydrate.columnSpecJson],
  );

  const state = useMemo<SectionedState>(() => ({
    sectionData: (workspace.policy_section_data ?? {}) as SectionedState['sectionData'],
    addedSections: (workspace.added_policy_sections ?? []) as SectionedState['addedSections'],
    order: workspace.policy_section_order,
  }), [workspace.policy_section_data, workspace.added_policy_sections, workspace.policy_section_order]);

  const handleChange = useCallback((next: SectionedState) => {
    setWorkspace({
      ...workspace,
      policy_section_data: next.sectionData as Record<string, PolicySectionEntry>,
      added_policy_sections: next.addedSections as NoteAddedPolicySection[],
      policy_section_order: next.order,
    });
  }, [workspace, setWorkspace]);

  return (
    <SectionedNarrativeEditor
      templateSections={templateSections}
      state={state}
      onChange={handleChange}
      readOnly={readOnly}
      contentVersion={contentVersion}
      railTitle="Policy sections"
    />
  );
};

function parseTemplateSections(columnSpecJson: string | null | undefined): SectionTemplate[] {
  if (!columnSpecJson) return [];
  try {
    const arr = JSON.parse(columnSpecJson);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((s: any) => s && typeof s === 'object' && typeof s.code === 'string')
      .map((s: any) => ({
        code: s.code,
        title: typeof s.title === 'string' ? s.title : s.code,
        defaultContent: s.default_content ?? null,
      }));
  } catch {
    return [];
  }
}

export default PolicyEditorPane;
